import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { LanguageStore } from '../language.store';
import { StepperService } from '../stepper.service';
import { SnackBarService } from '../snack-bar.service';
import { Router } from '@angular/router';
import { catchError, finalize } from 'rxjs';
import { PricingInformations } from '../../../core/api/models/pricing-informations';
import {
  AcsBeneficiaryClientModel,
  AcsProjectModel,
  SaveExpatProjectModel
} from '../../model/acs-project.model';
import { ExpatPricing } from '../../../core/api/models/expat-pricing';
import { FormGroup } from '@angular/forms';
import { Routing } from '../../../app.routes';
import { ExpatClient } from '../../../core/api/models/expat-client';
import { AuthStateService } from '../state/auth-state.service';
import { ReferenceStateService } from '../state/reference-state.service';
import { ProjectStateService } from '../state/project-state.service';
import { SubscriptionFeatureService } from '../feature/subscription-feature.service';
import { TokenResponse } from '../http/dto';
import { IAcsHttpService } from '../http/acs/acs-http-service.interface';
import { ACS_HTTP_SERVICE } from '../http/http-service.token';

@Injectable({
  providedIn: 'root'
})
export class TarificationsComponentService {
  private authStateService: AuthStateService = inject(AuthStateService);
  private referenceStateService: ReferenceStateService = inject(
    ReferenceStateService
  );
  readonly acsHttpService: IAcsHttpService = inject(ACS_HTTP_SERVICE);
  private projectStateService: ProjectStateService =
    inject(ProjectStateService);
  private languageStore: LanguageStore = inject(LanguageStore);
  private stepperService: StepperService = inject(StepperService);
  private snackBarService: SnackBarService = inject(SnackBarService);
  private router: Router = inject(Router);
  readonly subscriptionFeatureService: SubscriptionFeatureService = inject(
    SubscriptionFeatureService
  );

  private isPriceLoading: WritableSignal<boolean> = signal(false);

  /** state select **/

  public selectIsPriceLoading() {
    return this.isPriceLoading;
  }

  /** Getter **/
  public getAcsProjectDataModel() {
    return this.referenceStateService.selectAcsProjectDataModel();
  }

  public getCurrentLanguageCode() {
    return this.languageStore.currentLanguageCode();
  }

  public getPricingInformations() {
    return this.projectStateService.getPricingInformations();
  }

  public getExpatPricing() {
    return this.projectStateService.selectExpatPricing();
  }

  public getExpatClientInfos() {
    return this.projectStateService.selectExpatClientInfos();
  }

  public getBeneficiaryHasMaternityOption() {
    return this.projectStateService.getBeneficiaryHasMaternityOption()!;
  }

  public getProjectBeneficiaries() {
    return this.projectStateService.selectProjectBeneficiaries();
  }

  /** set and update **/

  public setIsPriceLoading(value: boolean) {
    this.isPriceLoading.set(value);
  }

  setExpatPricing(data: ExpatPricing) {
    this.projectStateService.setExpatPricing(data);
  }

  public setBeneficiaryHasMaternityOption(category: string) {
    this.projectStateService.setBeneficiaryHasMaternityOption(category);
  }

  public updatePricingInformations(
    formValue: Partial<PricingInformations> & { currency?: string }
  ) {
    this.projectStateService.updatePricingInformations(formValue);
  }

  public updateExpatClientInfos(
    infoToUpdate: Partial<AcsBeneficiaryClientModel>,
    forceUpdateBeneficiaries: boolean = false
  ) {
    this.projectStateService.updateExpatClientInfos(
      infoToUpdate,
      forceUpdateBeneficiaries
    );
  }

  public postPricing(formatData: AcsProjectModel) {
    return this.acsHttpService.postExpatTarificationCalculate(formatData);
  }

  public submitStep3(form: FormGroup, formatData: AcsProjectModel) {
    this.isPriceLoading.set(true);
    this.subscriptionFeatureService.setIsLoading(true);
    this.projectStateService.updatePricingInformations(form.value);
    if (this.projectStateService.selectIsNewProject()) {
      this.subscriptionFeatureService
        .validateSubmitStep3(form)
        .pipe(
          finalize(() => {
            this.isPriceLoading.set(false);
            this.subscriptionFeatureService.setIsLoading(false);
          }),
          catchError((err) => {
            this.snackBarService.errorMessage(err.error.message);
            throw err;
          })
        )
        .subscribe((data) => {
          this.afterSubmitAction(data);
        });
    } else {
      const token =
        this.authStateService.getSessionStorageUrlParamInfo()?.token;
      const projectUuid =
        this.authStateService.getSessionStorageUrlParamInfo()?.uuid;
      if (token && projectUuid) {
        this.subscriptionFeatureService
          .validateSubmitStep3(form)
          .pipe(
            finalize(() => {
              this.isPriceLoading.set(false);
              this.subscriptionFeatureService.setIsLoading(false);
            }),
            catchError((err) => {
              this.snackBarService.errorMessage(err.error.message);
              throw err;
            })
          )
          .subscribe((data) => {
            this.afterSubmitAction(data);
          });
      }
    }
  }

  private afterSubmitAction(data: TokenResponse) {
    //Redirection vers la page de sauvegarde de devis
    this.stepperService.step.set(3);
    this.authStateService.updateData(data);
    this.projectStateService.setIsNewProject(false);
    this.projectStateService.setGuardPath(Routing.SOUSCRIPTION);
    this.router.navigate([Routing.SOUSCRIPTION]).then();
  }

  public saveStep3(formatData: AcsProjectModel, form: FormGroup) {
    this.isPriceLoading.set(true);
    this.subscriptionFeatureService.setIsLoading(true);
    if (this.projectStateService.selectIsNewProject()) {
      this.validateSaveStep3(form);
    } else {
      const token =
        this.authStateService.getSessionStorageUrlParamInfo()?.token;
      const projectUuid =
        this.authStateService.getSessionStorageUrlParamInfo()?.uuid;
      if (token && projectUuid) {
        this.authStateService.saveUuidToken({
          token: token,
          uuid: projectUuid
        });
        this.validateSaveStep3(form);
      }
    }
  }

  private validateSaveStep3(form: FormGroup) {
    this.subscriptionFeatureService
      .validateSaveStep3(form)
      .pipe(
        finalize(() => {
          this.isPriceLoading.set(false);
          this.subscriptionFeatureService.setIsLoading(false);
        }),
        catchError((err) => {
          this.snackBarService.errorMessage(err.error.message);
          throw err;
        })
      )
      .subscribe(() => {
        //Redirection vers la page de sauvegarde de devis
        this.projectStateService.setIsNewClient(false);
        this.router.navigate([Routing.DEVISSAUVEGARDE]).then();
      });
  }

  private formatForSave(
    subscription: boolean,
    formatData: AcsProjectModel,
    form: FormGroup
  ): SaveExpatProjectModel {
    const client = this.projectStateService.selectExpatClientInfos()!;
    const formattedData = formatData;
    const beneficiaries = this.projectStateService.selectProjectBeneficiaries();
    beneficiaries.forEach(
      (item) =>
        (item.acs_expat_beneficiary_maternity_option =
          item.category === form.get('beneficiaries')!.value ? 1 : 0)
    );
    const clientToSend: ExpatClient = {
      address_1: client.address_1,
      address_2: client.address_2,
      birth_date: client.birth_date,
      city: client.city,
      country: client.country,
      email: client.email,
      first_name: client.first_name,
      language: client.language,
      last_name: client.last_name,
      phone: client.phone,
      postal_code: client.postal_code,
      terms_accepted: client.terms_accepted,
      data_consent_commercial: client.data_consent_commercial,
      title: client.title
    };

    this.projectStateService.updateExpatClientInfos(
      {
        ...clientToSend,
        beneficiaries: [...beneficiaries]
      },
      true
    );
    this.projectStateService.setProjectBeneficiaries(beneficiaries);
    return {
      pricing: formattedData.pricing,
      beneficiaries: beneficiaries,
      client: clientToSend,
      producer_code: this.authStateService.selectProducerCode()()!,
      mode: this.authStateService.selectAcsMode()(),
      product: form.get('currency')!.value,
      token: this.authStateService.getSessionStorageUrlParamInfo()?.token
        ? this.authStateService.getSessionStorageUrlParamInfo()!.token!
        : undefined,
      subscription: subscription
    };
  }
}
